import React, { Component } from 'react';
import { ApplicationState } from 'store/rootReducer';
import {
  changeTransactionPageSize,
  cloneTransactionId,
  closeAndDeleteBlankTransactionThunk,
  createTransactionAction,
  deleteTransactionById,
  editorTabChanged,
  expandTransactionRowAction,
  getTransactions,
  selectTransactions,
  sortTransactionsAction,
  updateActivePage,
  updateTransactionAction,
} from 'store/transactions/transactionsActions';
import { getDefaultServiceId } from 'store/ui/service/serviceActions';
import { connect } from 'react-redux';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import { WithTranslation, withTranslation } from 'react-i18next';
import BlazeTable from 'components/blazeTable/BlazeTable';
import _ from 'lodash';
import { Transaction, TransactionWrapper } from 'types/rest/assetCatalogTypes';
import TransactionEditor, {
  EditorTabType,
  EmptyTransaction,
} from 'views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import ExpandLink from '../../../../components/blazeTable/ExpandLink';
import i18n from '../../../../i18n';
import { confirm } from '../../../../components/ui/modal/BlazeConfirmationModal';
import { getColoredHttpMethod } from '../../../../utils/commonUtil';
import BzmIcon from '../../../../components/icons/BzmIcon';
import SimpleTagList from '../../../../components/ui/tags/SimpleTagList';
import MessagingEditor from '../TransactionEditor/MessagingEditor';
import VirtualServiceTypeIcon from '../../../../components/icons/VirtualServiceTypeIcon';
import { isFeatureEnabled } from '../../../../utils/bzmUserConfigUtil';
import TransactionSandboxBtn from './TransactionSandboxBtn';

type AllProps = StateProps & DispatchProps & WithTranslation;

class TransactionsBlazeTable extends Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();

  componentDidMount() {
    this.props.getTransactions();
  }

  onTransactionSave = (
    transactionWrapper: TransactionWrapper,
    isCreate: boolean,
    selectedEditorTab?: EditorTabType,
  ) => {
    if (isCreate) {
      this.props.createTransaction(transactionWrapper, selectedEditorTab);
    } else {
      this.props.updateTransaction(transactionWrapper);
    }
  };

  componentDidUpdate(prevProps: Readonly<AllProps>): void {
    if (prevProps.isCreatingNewTransaction !== this.props.isCreatingNewTransaction) {
      this.tableRef.expandRowHandler(-1);
    }
  }

  duplicateRow = (transaction: Transaction) => {
    this.props.cloneTransactionId(transaction);
  };

  // MOB-47723: open the transaction expanded on the version History tab
  openHistory = (transaction: Transaction) => {
    this.props.onEditorTabChanged('history');
    this.tableRef.expandRowHandler(transaction.id);
  };

  deleteRowClicked = (transaction: Transaction) => {
    if (this.isTransactionNew(transaction)) {
      this.props.closeAndDeleteBlankTransactionThunk();
    } else {
      const modalBody = (
        <div>
          {i18n.t('transactions:table.inlineDeleteWarn')} <strong className="text-break">{transaction.name}</strong>?
        </div>
      );
      confirm({
        confirmation: modalBody,
        yesOptionTitle: i18n.t('common:actions.delete'),
        title: i18n.t('transactions:table.deleteTxn'),
      })
        .then(() => {
          this.props.deleteTransactionById(transaction);
        })
        .catch(() => {
          // User chose "No"
        });
    }
  };

  // Definition of table columns
  buildColsDefinition = (): BlazeTableColDef[] => {
    const idColumn: BlazeTableColDef = {
      accessor: 'id',
      cellComponent: (transaction: Transaction) => {
        if (this.isTransactionNew(transaction)) {
          return '-';
        }
        return transaction.id;
      },
      header: 'ID',
      width: 80,
    };

    const methodColumn: BlazeTableColDef = {
      accessor: 'method',
      cellComponent: (transaction: Transaction) => {
        if (this.isTransactionNew(transaction) || transaction.type === 'MESSAGING') {
          return '-';
        }
        return getColoredHttpMethod(
          'method' in transaction.dsl.requestDsl ? (transaction.dsl.requestDsl.method as any) : '-',
        );
      },
      header: 'Method',
      isSortable: false,
      width: 100,
    };

    const urlColumn: BlazeTableColDef = {
      accessor: 'url',
      cellComponent: (transaction: Transaction) => {
        if (this.isTransactionNew(transaction) || transaction.type === 'MESSAGING') {
          return '-';
        }
        return <span>{'url' in transaction.dsl.requestDsl ? transaction.dsl.requestDsl.url.matchingValue : '-'}</span>;
      },
      header: 'Request URL',
      isSortable: false,
    };

    const responseStatusColumn: BlazeTableColDef = {
      accessor: 'dsl.responseDsl.status',
      cellComponent: (transaction: Transaction) => {
        if (this.isTransactionNew(transaction) || transaction.type === 'MESSAGING') {
          return '-';
        }
        return <span>{'status' in transaction.dsl.responseDsl ? transaction.dsl.responseDsl.status : '-'}</span>;
      },
      header: 'Response Status',
      isSortable: false,
      width: 130,
    };

    const serviceNameColumn: BlazeTableColDef = {
      accessor: 'serviceName',
      cellComponent: (transaction: Transaction) => {
        if (this.isTransactionNew(transaction)) {
          return '-';
        }
        return <span>{transaction.serviceName}</span>;
      },
      header: 'Service',
      isSortable: false,
      width: 200,
    };

    const tagsColumn: BlazeTableColDef = {
      accessor: 'tags',
      cellComponent: (transaction: Transaction) => {
        return (
          <div className={`qa-tbl-row-transaction-tags-${transaction.id} picked`}>
            {transaction.tags != null && <SimpleTagList tags={transaction.tags} />}
          </div>
        );
      },
      isSortable: false,
      header: 'Tags',
      width: 180,
    };

    const actionsColumn: BlazeTableColDef = {
      accessor: '',
      cellComponent: (transaction: Transaction) => {
        return (
          <>
            <span className="flex-table-key">${i18n.t('transactions:table.actionsColumn')}</span>
            <div className="flex-table-actions">
              {!this.isTransactionNew(transaction) && (
                <button
                  disabled={this.props.isPrivateCloudReadOnly}
                  id={`qa-tbl-row-clone-transaction-${transaction.id}`}
                  className="btn btn-link-dark btn-no-hover mr-2"
                  title={i18n.t('transactions:table.cloneTxn')}
                  onClick={() => this.duplicateRow(transaction)}
                >
                  <BzmIcon variant={'icon-bzm-copy'} className={'icon-xs'}></BzmIcon>
                  <span className="sr-only">${i18n.t('transactions:table.cloneTxn')}</span>
                </button>
              )}

              {transaction.type === 'HTTP' && (
                <TransactionSandboxBtn disabled={this.isTransactionNew(transaction)} transaction={transaction} />
              )}

              <button
                disabled={this.props.isPrivateCloudReadOnly}
                id={`qa-tbl-row-delete-transaction-${transaction.id}`}
                className="btn btn-link-dark btn-no-hover"
                title={i18n.t('transactions:table.deleteTxn')}
                onClick={() => this.deleteRowClicked(transaction)}
              >
                <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'}></BzmIcon>
                <span className="sr-only">${i18n.t('transactions:table.deleteTxn')}</span>
              </button>
            </div>
          </>
        );
      },
      header: 'Actions',
      isSortable: false,
      width: 90,
    };

    const colsDefinition: BlazeTableColDef[] = [];
    colsDefinition.push(idColumn);

    const nameColumnWithIcon: BlazeTableColDef = {
      accessor: 'name',
      cellComponent: (transaction: Transaction) => {
        const icon = <VirtualServiceTypeIcon type={transaction.type} className={'mr-3 align-middle'} />;
        if (this.isTransactionNew(transaction)) {
          return <div className={'d-flex'}>{icon} - </div>;
        }
        return (
          <div className={'d-flex'}>
            {icon}
            <ExpandLink
              id={transaction.id ? transaction.id : ''}
              value={transaction.name}
              onToggleExpandDetail={() => this.tableRef.expandRowHandler(transaction.id)}
            />
          </div>
        );
      },
      header: 'Name',
      width: 280,
    };
    colsDefinition.push(nameColumnWithIcon);
    colsDefinition.push(methodColumn);
    colsDefinition.push(urlColumn);
    colsDefinition.push(responseStatusColumn);
    colsDefinition.push(serviceNameColumn);
    colsDefinition.push(tagsColumn);
    colsDefinition.push(actionsColumn);

    return colsDefinition;
  };
  ExpandDetailComponent = (transaction: Transaction) => {
    if (transaction.type === 'HTTP') {
      return (
        <TransactionEditor
          transaction={transaction}
          isResponseBodyMagicStringInvalid={this.props.isResponseBodyMagicStringInvalid}
          isResponseHeaderMagicStringInvalid={this.props.isResponseHeaderMagicStringInvalid}
          serviceId={transaction.serviceId}
          onSave={this.onTransactionSave}
          isCreate={this.isTransactionNew(transaction)}
          isReadOnly={this.props.isPrivateCloudReadOnly}
          resHeaderInvalidMagicStrings={this.props.resHeaderInvalidMagicStrings}
          resBodyInvalidMagicStrings={this.props.resBodyInvalidMagicStrings}
          onTabChanged={this.props.onEditorTabChanged}
          selectedEditorTab={this.props.selectedEditorTab}
        />
      );
    } else {
      return (
        <MessagingEditor
          transaction={transaction}
          isResponseBodyMagicStringInvalid={this.props.isResponseBodyMagicStringInvalid}
          isResponseHeaderMagicStringInvalid={this.props.isResponseHeaderMagicStringInvalid}
          serviceId={transaction.serviceId}
          onSave={this.onTransactionSave}
          isCreate={this.isTransactionNew(transaction)}
          isReadOnly={this.props.isPrivateCloudReadOnly}
          resHeaderInvalidMagicStrings={this.props.resHeaderInvalidMagicStrings}
          resBodyInvalidMagicStrings={this.props.resBodyInvalidMagicStrings}
          onTabChanged={this.props.onEditorTabChanged}
          selectedEditorTab={this.props.selectedEditorTab}
        />
      );
    }
  };

  prepareData = (): Transaction[] => {
    const transactions: Transaction[] = _.clone(this.props.transactions);
    if (this.props.isCreatingNewTransaction) {
      transactions.unshift(new EmptyTransaction(this.props.typeOfNewTransaction, this.props.getDefaultServiceId()));
    }
    return transactions;
  };

  isTransactionNew = (transaction: Transaction) => {
    return transaction.id === -1;
  };

  getTableOptions = (): BlazeTableOptions => {
    return {
      expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
      selectAllDropdown: true,
      showRowExpander: true,
      columnDef: this.buildColsDefinition(),
      pageable: true,
      selectable: true,
      pageSize: 10,
      rowPadding: 8,
      loadingText: this.props.loadingText,
      tableName: 'transactions',
      noResultsText: this.props.t('transactions:txnTable.noTxnsAvailable'),
      minWidth: 900,
      sortable: true,
      paginationOverride: {
        activePage: this.props.pageable.skip,
        pageSize: this.props.pageable.limit,
        total: this.props.totalTransactions,
      },
      sortingOverride: this.props.sort,
      selectAllType: this.props.selectAllType,
    };
  };

  render() {
    return (
      <BlazeTable
        onExpandedItemChange={(id: number | null) => {
          if (this.props.expandedTransactionId === id) {
            this.props.expandTransaction(null);
          } else {
            this.props.expandTransaction(id);
          }
        }}
        expandedItem={this.props.expandedTransactionId}
        selectedItems={this.props.selectedTransactions}
        onSelectionChange={this.props.selectTransactions}
        data={this.prepareData()}
        options={this.getTableOptions()}
        isLoading={this.props.isLoading}
        onRef={(ref: any) => (this.tableRef = ref)}
        onSort={sortConfig => {
          //Change sort & fetch
          this.props.sortTransactionsAction(sortConfig);
          this.props.getTransactions();
        }}
        showPageSizeDropdown={true}
        onPageChange={pageNumber => this.props.updateActivePage(pageNumber)}
        onPageSizeChange={pageSize => this.props.changeTransactionPageSize(pageSize)}
      />
    );
  }
}

const mapStateToProps = ({ transactions, blazeUserConfig }: ApplicationState) => {
  return {
    isCreatingNewTransaction: transactions.isCreatingNewTransaction,
    typeOfNewTransaction: transactions.creatingNewTransactionType,
    isLoading: transactions.isLoading,
    loadingText: transactions.loadingText,
    pageable: transactions.pageable,
    transactions: transactions.filteredTransactions,
    totalTransactions: transactions.totalFilteredTransactions,
    expandedTransactionId: transactions.expandedTransactionId,
    selectedTransactions: transactions.selectedTransactions,
    sort: transactions.sort,
    isPrivateCloudReadOnly: blazeUserConfig.isPrivateCloudReadOnly,
    resHeaderInvalidMagicStrings: transactions.resHeaderInvalidMagicStrings,
    resBodyInvalidMagicStrings: transactions.resBodyInvalidMagicStrings,
    isResponseBodyMagicStringInvalid: transactions.isResponseBodyMagicStringInvalid,
    isResponseHeaderMagicStringInvalid: transactions.isResponseHeaderMagicStringInvalid,
    selectAllType: transactions.selectAllType,
    selectedEditorTab: transactions.selectedEditorTab,
  };
};

const mapDispatchToProps = {
  changeTransactionPageSize,
  updateActivePage,
  getDefaultServiceId,
  getTransactions,
  selectTransactions,
  cloneTransactionId,
  deleteTransactionById,
  closeAndDeleteBlankTransactionThunk,
  sortTransactionsAction,
  transactionSortChanged: sortTransactionsAction,
  updateTransaction: updateTransactionAction,
  createTransaction: createTransactionAction,
  expandTransaction: expandTransactionRowAction,
  onEditorTabChanged: editorTabChanged,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('transactions')(connect(mapStateToProps, mapDispatchToProps)(TransactionsBlazeTable));
